import {Component, OnDestroy, OnInit} from '@angular/core';
import {PrlLanguageService} from '../../../prl-language/services/prl-language.service';
import {PrlDashboardGraphDataService} from '../../services/graph/prl-dashboard-graph-data.service';
import {combineLatestAll, of, Subject} from 'rxjs';
import {debounceTime, distinct, filter, takeUntil} from 'rxjs/operators';

import {
    FieldUiDataObj,
    FilterForm,
    GraphInputMxObj,
    MxButtonModel,
    MxFormCustomizerEvent,
    MxGraphAdvancedFiltersDialogComponent,
    MxPreloaderService,
    MxReportFieldsConverterService,
    OnBoFormReady,
    PreloaderConfig,
    ReportFieldMxObj,
    ReportUiDataObj,
    withPreloader
} from 'mx-core';
import {FormControl, FormGroup} from '@angular/forms';
import {KeyValueObj} from '../../../../../../../mx-core/src/generated/generated-core.model';

import {MatDialog} from '@angular/material/dialog';
import moment from 'moment/moment';
import {PrlGeneratorService} from '../../../../shared/services/prl-generator.service';

import * as Highcharts from 'highcharts';

@Component({
    selector: 'prl-dashboard-graph',
    standalone: false,
    templateUrl: './prl-dashboard-graph.component.html',
    styleUrl: './prl-dashboard-graph.component.scss'
})
export class PrlDashboardGraphComponent implements OnInit, OnDestroy, OnBoFormReady {
    chartInstance!: Highcharts.Chart;

    isUpdateChart: boolean = false;
    chartOptions: Highcharts.Options;
    location: string = 'center';
    noDataLabel: Highcharts.SVGElement | null = null;


    graphIndex: number = 0;
    initialized: boolean = false;
    maxSize: number;
    tooManyRaws: boolean = false;

    advancedFieldsButton: MxButtonModel;
    refreshButton: MxButtonModel;
    selectedCampaignControl: FormControl = new FormControl('');
    selectedGraphControl: FormControl = new FormControl('');

    preloaderName: string = 'graph-body';
    preloaderConfig: PreloaderConfig = {name: this.preloaderName, type: 'RoundSpinner'};

    protected graphs: Array<KeyValueObj> = [];
    protected campaigns: Array<KeyValueObj> = [];
    protected advancedFields: Array<FieldUiDataObj> = [];
    protected graphInput: Array<ReportUiDataObj> = [];
    protected dynamicFields: Array<FieldUiDataObj> = [];

    private destroy$$: Subject<null> = new Subject<null>();
    private readonly filterKey: string = 'graphFilters';
    private mxFormCustomizerEvent: MxFormCustomizerEvent;

    constructor(
        private graphService: PrlDashboardGraphDataService,
        private generatorService: PrlGeneratorService,
        private dialog: MatDialog,
        private languageService: PrlLanguageService,
        private converterService: MxReportFieldsConverterService,
        private preloader: MxPreloaderService
    ) {
    }

    ngOnDestroy(): void {
        this.destroy$$.next(null);
    }

    ngOnInit(): void {
        this.chartOptions = this.getGraphInitConfig();
        this.refreshButton = {
            tooltip: 'Refresh graph',
            icon: 'refresh',
            className: 'mxs-button--round mxs-button--primary',
            clickHandler: this.refreshGraph
        };
        this.advancedFieldsButton = {
            tooltip: 'Advanced fields',
            icon: 'tune',
            className: 'mxs-button--round mxs-button--primary',
            clickHandler: this.openAdvancedFilters
        };

        of(
            this.graphService.getGraphsList(this.languageService.getLanguage()))
            .pipe(takeUntil(this.destroy$$), withPreloader(this.preloader, this.preloaderConfig), combineLatestAll())
            .subscribe((response) => {
                let data = response[0];
                for (const graph of data) {
                    if (graph) {
                        this.graphs.push({
                            key: graph.id,
                            value: graph.name,
                            dependOn: null
                        });
                        graph.campaigns.forEach(campaign => {
                            const exists = this.campaigns.some(c => c.key === campaign.id);
                            if (!exists) {
                                this.campaigns.push({
                                    key: campaign.id,
                                    value: campaign.name,
                                    dependOn: null
                                });
                            }
                        });
                    }
                }

                this.selectedGraphControl.setValue(this.graphs[0]);
                this.selectedCampaignControl.setValue(this.campaigns[0]);
                this.getGraphData();
                this.initialized = true;
            });
    };

    onCustomizeFilters(event: MxFormCustomizerEvent): void {
        event.customizeFieldConfigs('statDate', {style: 'double-width-column'});
        this.mxFormCustomizerEvent = event;
        event.addCustomizer(this);
    }

    boOnFormReady(form: FormGroup): void {
        form.valueChanges.pipe(
            distinct(),
            takeUntil(this.destroy$$),
            debounceTime(1000),
            filter(() => form.dirty)
        ).subscribe (value => {
            this.saveFilter(value);
            this.getGraphData(value);
        });
    }

    refreshGraph = () => {
        this.getGraphData();
    };

    openAdvancedFilters = () => {
        return this.dialog.open(MxGraphAdvancedFiltersDialogComponent, {
            panelClass: 'mxs-dialog',
            minWidth: '30vw',
            data: {
                advancedFields: this.advancedFields
            }
        }).afterClosed().subscribe((formData) => {
            this.saveFilter(formData);
            this.getGraphData(formData);
        });
    };

    getLabel = (label: string): string => {
        return this.languageService.getLabel(label);
    };

    loadSelectedGraph(): void {
        let graphIndex = this.getCurrentlySelectedGraphIndex();
        let campaignIndex = this.getCurrentlySelectedCampaignIndex();
        this.graphIndex = graphIndex;
        this.selectedGraphControl.setValue(this.graphs[graphIndex], {onlySelf: false});
        this.selectedCampaignControl.setValue(this.campaigns[campaignIndex], {onlySelf: false});
        this.getGraphData(null, true);
    }

    getCurrentlySelectedGraphIndex(): number {
        return this.graphs.findIndex((graph) => graph['key'] === this.selectedGraphControl.value['key']);
    }

    getCurrentlySelectedCampaignIndex(): number {
        return this.campaigns.findIndex((campaign) => campaign['key'] === this.selectedCampaignControl.value['key']);
    }

    getGraphName(graphIndex: number): string {
        return this.graphs[graphIndex].value;
    }

    getGraphTitle(): string {
        return this.selectedGraphControl.value.value;
    }

    private getGraphInitConfig(): any {
        let options: any = ({
            chart: {
                type: 'column',
                height: 440,
            },
            noData: {
                style: {
                    display: 'none'
                }
            },
            credits: {enabled: false},
            title: {
                text: ''
            },
            series: [],
            loading: false,
            xAxis: {
                categories: [],
                gridLineWidth: 1,
                labels: {
                    overflow: 'justify',
                    maxStaggerLines: 1
                }
            },
            yAxis: [
                {
                    title: {
                        text: ''
                    },
                    labels: {
                        overflow: false,
                        maxStaggerLines: 1,
                        x: -25
                    },
                    opposite: false
                },
                {
                    title: {
                        text: ''
                    },
                    labels: {
                        overflow: false,
                        maxStaggerLines: 1,
                        x: 25
                    },
                    opposite: true
                }
            ],
            exporting: {enabled: false},
            plotOptions: {
                column: {
                    stacking: 'normal',
                    depth: 50
                },
                pie: {
                    dataLabels: {
                        enabled: true,
                        format: '<b>{point.name}</b>: {point.percentage:.1f} %',
                        style: {
                            color: 'black'
                        }
                    },
                    allowPointSelect: true,
                    cursor: 'pointer',
                    depth: 35
                }
            }
        });

        return options;
    }

    private saveFilter(filters: any): void {
        let oldFilters = sessionStorage.getItem(this.filterKey);
        let filtersObject = {};
        if (oldFilters) {
            filtersObject = JSON.parse(oldFilters);
        }
        Object.keys(filters).forEach(key => {
            if (filters[key] instanceof moment) {
                const momentDate = moment(filters[key]);
                filters[key] = momentDate.toISOString(true);
            }
        });
        if (filtersObject[this.getGraphName(this.graphIndex)]) {
            Object.assign(filtersObject[this.getGraphName(this.graphIndex)], filters);
        } else {
            filtersObject[this.getGraphName(this.graphIndex)] = filters;
        }
        sessionStorage.setItem(this.filterKey, JSON.stringify(filtersObject));
    }

    private loadFilters(): any {
        let filtersObject = sessionStorage.getItem(this.filterKey);
        if (filtersObject) {
            return JSON.parse(filtersObject)[this.getGraphName(this.graphIndex)];
        }
    }

    private resetFilters(): void {
        let filters = this.loadFilters();
        if (this.mxFormCustomizerEvent) {
            this.mxFormCustomizerEvent.fieldsData.next({
                objectMetaSource: {
                    fieldsData: this.dynamicFields
                },
                initialData: filters
            });
        }
    }

    private initGraphInputs(inputs: ReportFieldMxObj[]): void {
        let dynamicFields: Array<ReportUiDataObj> = [];
        let advancedFields: Array<ReportUiDataObj> = [];
        this.graphInput = inputs.map(MxReportFieldsConverterService.convertToEntity);
        inputs.forEach((field: ReportFieldMxObj) => {
            let dynamicField = MxReportFieldsConverterService.convertToEntity(field);
            if (this.converterService.isDateRange(dynamicField)) {
                dynamicField.value = dynamicField.sublist[0].value;
            }
            if (field.internal) {
                advancedFields.push(dynamicField);
            } else {
                dynamicFields.push(dynamicField);
            }
        });
        this.dynamicFields = dynamicFields.map(value => this.converterService.convertToUiData(value));
        this.advancedFields = advancedFields.map(value => this.converterService.convertToUiData(value));
        let filters = this.loadFilters();
        if (filters) {
            this.advancedFields.forEach(field => {
                if (filters[field.name]) {
                    field.defaultValue = filters[field.name];
                }
            });
        }
        this.resetFilters();
    }

    private showPreloader = (show: boolean) => {
        show ? this.preloader.show(this.preloaderConfig) : this.preloader.hide(this.preloaderConfig);
        this.updateControls(!show);
    };

    private updateControls(active: boolean) {
        if (active) {
            this.selectedGraphControl.enable();
            this.selectedCampaignControl.enable();
        } else {
            this.selectedGraphControl.disable();
            this.selectedCampaignControl.disable();
        }
        this.refreshButton.disabled = !active;
        this.advancedFieldsButton.disabled = !active;
    }

    onChartInstance: Highcharts.ChartCallbackFunction = ($event) => {
        this.chartInstance = $event;
    };

    getGraphInputWithFilterValues(filters: Array<FilterForm>): ReportFieldMxObj[] {
        let graphInput = [];

        this.graphInput.forEach((input) => {
            let tmp: any = {};
            tmp.name = input.field;
            tmp.type = input.type;
            if (this.converterService.isDateRange(input)) {
                tmp.name = input.field;
                tmp.value = filters.find((filterForm: FilterForm) => filterForm.field === input.sublist[0].field).value;
                tmp.sublist = [];
                tmp.sublist[0] = {
                    name: input.sublist[0].field,
                    value: tmp.value,
                    type: 'list'
                };
                tmp.sublist[1] = {
                    name: input.sublist[1].field,
                    value: filters.find((filterForm: FilterForm) => filterForm.field === input.sublist[1].field).value,
                    type: 'date'
                };
                tmp.sublist[2] = {
                    name: input.sublist[2].field,
                    value: filters.find((filterForm: FilterForm) => filterForm.field === input.sublist[2].field).value,
                    type: 'date'
                };
            } else {
                tmp.value = filters.find((filterForm: FilterForm) => filterForm.field === input.field).value;
            }
            graphInput.push(tmp);
        });
        return graphInput;
    }

    private getGraphData(formData?: any, initial: boolean = false) {
        if (this.graphs.length === 0) {
            return;
        }

        this.showPreloader(true);
        let fields: Array<FieldUiDataObj> = [...this.dynamicFields, ...this.advancedFields];
        let filters = this.generatorService.getFiltersFromGraphFormData(this.graphInput, fields, formData);
        let graphInput = initial ? [] : this.getGraphInputWithFilterValues(filters);
        let graph: Partial<GraphInputMxObj> = {
            campaignId: this.selectedCampaignControl.value['key'],
            graphId: this.selectedGraphControl.value['key'],
            input: graphInput,
            language: this.languageService.getLanguage()
        };

        this.graphService.getGraphData(graph)
            .subscribe({
                next: (result:any) => {
                    let options: Highcharts.Options = this.getGraphInitConfig();
                    if (result.isTooManyRows) {
                        options.lang = {noData: this.getLabel('label.dashboard.errors.too.many.values')};
                        this.maxSize = result.categories.length;
                    }

                    this.tooManyRaws = result.isTooManyRows;
                    let graphType: string;
                    this.initGraphInputs(result.input);

                    if (!result.categories || !result.series) {
                        return;
                    }

                    if (result.categories) {
                        options.xAxis = {categories: result.categories.slice()};
                    }

                    if (result.series) {
                        if (!result.series.length) {
                            if (this.noDataLabel) {
                                this.noDataLabel.destroy();
                                this.noDataLabel = undefined;
                            }

                            const chart = this.chartInstance;
                            const centerX = chart.chartWidth / 2;
                            const centerY = chart.chartHeight / 2;

                            this.noDataLabel = this.chartInstance.renderer.text(
                                this.getLabel('label.errors.no.data.to.display'), centerX, centerY)
                                .attr({align: 'center'})
                                .css({color: '#666', fontSize: '16px'})
                                .add();
                        }
                        graphType = 'column';
                        let alphaValue = 0;
                        let betaValue = 25;
                        let depthValue = 70;
                        let disable3d = false;

                        for (let i = 0; i < result.series.length; i++) {
                            let resultData: Array<[string, number]> = [];
                            for (let j = 0; j < result.series[i].data.length; j++) {
                                let strval: string = result.series[i].data[j][0];
                                let numval: number = result.series[i].data[j][1];
                                resultData.push([strval, numval]);
                            }

                            let type: string = result.series[i].type;

                            let serie: any = {
                                type: type,
                                name: result.series[i].name,
                                yAxis: result.series[i].yAxis,
                                data: resultData,
                                allowPointSelect: true
                            };

                            if (type === 'bar') {
                                graphType = 'bar';
                                alphaValue = 10;
                                betaValue = 0;
                                depthValue = 40;
                            } else if (type === 'pie') {
                                graphType = 'pie';
                                alphaValue = 45;
                                betaValue = 0;
                                depthValue = 35;

                                serie.dataLabels = {
                                    enabled: true,
                                    formatter: function (): string {
                                        let displayName = this.point.name;
                                        if (!displayName || displayName === '') {
                                            displayName = '-';
                                        }

                                        return '<b>' + displayName + '  </b>: ' + this.percentage.toFixed(1) + '%';
                                    },
                                    style: {
                                        color: 'black'
                                    }
                                };
                            } else if (type === 'line' || type === 'area') {
                                disable3d = true;
                            }
                            options.series.push(serie);
                        }

                        if (disable3d) {
                            alphaValue = 0;
                            betaValue = 0;
                            depthValue = 0;
                            options.yAxis[1].labels.x = 25;
                        } else {
                            options.yAxis[1].labels.x = 45;
                        }

                        options.chart.type = graphType;
                        options.chart.options3d = {
                            enabled: true,
                            alpha: alphaValue,
                            beta: betaValue,
                            depth: depthValue
                        };
                    }
                    if (graphType === 'column' && result.categories === null) {
                        options.tooltip = {
                            enabled: true,
                            animation: true,
                            backgroundColor: 'rgba(249, 249, 249, .85)',
                            borderWidth: 1,
                            borderRadius: 3,
                            dateTimeLabelFormats: {
                                millisecond: '%A, %b %e, %H:%M:%S.%L',
                                second: '%A, %b %e, %H:%M:%S',
                                minute: '%A, %b %e, %H:%M',
                                hour: '%A, %b %e, %H:%M',
                                day: '%A, %b %e, %Y',
                                week: 'Week from %A, %b %e, %Y',
                                month: '%B %Y',
                                year: '%Y'
                            },
                            formatter: function (): string {
                                return '<span style="color:' + this.series.color + '">\u25CF</span> '
                                    + this.series.name + ': <b>' + this.y + '</b><br/>';
                            },
                            shadow: true,
                            snap: 10,
                            style: {
                                color: '#333333',
                                cursor: 'default',
                                fontSize: '12px',
                                padding: '8px',
                                pointerEvents: 'none',
                                whiteSpace: 'nowrap'
                            }
                        };
                    }
                    this.chartOptions = options;
                    this.isUpdateChart = true;


                    this.showPreloader(false);
                },
                error: (error) => {
                    console.error(error);
                    let options = this.getGraphInitConfig();
                    options.lang = {noData: this.getLabel('label.no.graph.or.report')};
                    this.chartOptions = options;
                    this.showPreloader(false);
                }
            });
    }

}
